import { z } from 'zod';
import { foldForSearch } from '../fold-for-search.js';
import { freeLabelSchema } from '../free-label.js';
import { uuidV7Schema } from '../uuid.js';
import { BUILDING_TYPES, BUILDING_TYPE_LABELS } from './building-type.js';
import { inquiryStatusSchema } from './status.js';
import { INQUIRY_PURPOSES, INQUIRY_PURPOSE_LABELS, INQUIRY_PURPOSE_OPTION_LABELS, inquiryPurposeLabel, } from './purpose.js';
/**
 * L'**import d'une liste de contrôles** — un tableur de porte-à-porte ou de campagne systématique
 * (xlsx ou CSV) qui devient des demandes, une par ligne (R-B8).
 *
 * ⚠️ **La source de la forme est le fichier métier** fourni par le développeur le 23/09/2026
 * (`docs/local_docs/exemples/exemple-import-liste-controle-systematique.xlsx`), au même rang que le
 * prototype : trois feuilles, dont une de légendes, et des colonnes nommées en toutes lettres.
 *
 * Deux temps, comme l'import PDF (D7) : on **lit** le fichier et l'humain choisit la feuille et
 * la correspondance des colonnes (`preview`), puis on **écrit** (`commit`). Aucun des deux ne
 * garde le fichier : l'écran le renvoie au second temps.
 */
/** Plafond de lignes d'un fichier — au-delà, ce n'est plus une campagne, c'est un export de base. */
export const MAX_IMPORT_ROWS = 5000;
/**
 * Plafond de colonnes d'une ligne. Le fichier métier en porte onze ; au-delà de deux cents, ce
 * n'est plus une liste de contrôles, et chaque colonne coûte un parcours de toutes les lignes.
 */
export const MAX_IMPORT_COLUMNS = 200;
/** Plafond d'un fichier importé, en octets. */
export const MAX_IMPORT_BYTES = 10 * 1024 * 1024;
/**
 * Ce qu'une colonne du tableur peut alimenter. Tout le reste est rapporté « non conservé ».
 *
 * - `reference` → `Inquiry.reference`, la « Réf. GHP » **telle qu'écrite** (M1) — vide, le serveur
 *   en attribue une comme à la création ;
 * - `caseReference` → `Inquiry.case_reference` (« Référence dossier », « Réf_Client ») ;
 * - `caseUrl` → `Inquiry.case_url`, le lien du dossier client (#454) : l'**adresse du lien
 *   hypertexte** de la cellule, que le tableau de GHP pose sur un numéro — son texte sinon (M21) ;
 * - `client` → `Inquiry.client_id`, le donneur d'ordre **de la ligne**, rapproché du répertoire par
 *   son nom ; vide, le client choisi pour l'import vaut ;
 * - `purpose` → `Inquiry.purpose`, le type **de la ligne** (« Objet », `readImportPurpose`) ; vide,
 *   le type choisi pour l'import vaut ;
 * - `municipality` → la **commune nouvelle**, rapprochée du référentiel par son nom — le code postal
 *   départage deux homonymes ;
 * - `postalCode` → `Address.postal_code`, le code **de la voie** : une commune nouvelle n'a qu'un
 *   code au référentiel quand ses communes déléguées gardent le leur (Montjean-sur-Loire en
 *   49570, Mauges-sur-Loire en 49110) — et ce départage ;
 * - `delegatedName` → `Address.delegated_name`, la colonne « Commune déléguée » du fichier métier —
 *   ce qui départage deux « rue de la Mairie » d'une même commune nouvelle ;
 * - `streetNumber` → `Address.street_number` (« N° ») ; vide ou non rattaché, le numéro est lu
 *   **en tête de l'adresse** (`splitLeadingStreetNumber`, M32) ;
 * - `street` → `Address.street`, **verbatim** (« Appart 1 - 1 RUE D'ANJOU ») hors ce numéro de
 *   tête : rien d'autre ne se redécoupe (M1, M32) ;
 * - `cadastralReference` → `Site.cadastral_reference` (M30) ;
 * - `sewerNetworkType` → `Site.sewer_network_type`, le **réseau du dossier** (M43), que la colonne
 *   « Unitaire ? » du tableau des demandes de GHP répond par oui ou non
 *   (`readImportSewerNetworkType`) ;
 * - `buildingType` → `Site.building_type` (M10), la colonne « Type » du tableau de GHP, écrite en
 *   toutes lettres (`readImportBuildingType`) ;
 * - `ownerLastName` / `ownerFirstName` / `ownerPhone` → le **propriétaire** (`Site.owner_id`,
 *   M15) — sans nom, son téléphone se garde (M24, #379) ;
 * - `comment` → `Inquiry.comment`, verbatim — la colonne sans titre du fichier métier, où le
 *   service note l'état d'un dossier (« NON CONF. 24/05/2024 »).
 *
 * ⚠️ **Ni la date de naissance, ni la civilité** : le modèle n'en a pas l'usage, et une donnée
 * personnelle sans usage ne s'importe pas (RGPD, minimisation). L'adresse du propriétaire en a un
 * depuis #350 — le rapport papier l'imprime (`Person.address`, `Person.municipality`) —, mais
 * l'import ne la lit pas encore.
 */
export const IMPORT_FIELDS = [
    'reference',
    'caseReference',
    'caseUrl',
    'client',
    'purpose',
    'municipality',
    'postalCode',
    'delegatedName',
    'streetNumber',
    'street',
    'cadastralReference',
    'sewerNetworkType',
    'buildingType',
    'ownerLastName',
    'ownerFirstName',
    'ownerPhone',
    'comment',
    // Le reste du tableau des demandes de GHP (#436) : la date, le contact, le demandeur payeur, et
    // la raison sociale et l'adresse du propriétaire.
    'requestedOn',
    'contactName',
    'contactPhone',
    'applicantLastName',
    'applicantFirstName',
    'applicantCompany',
    'applicantEmail',
    'ownerCompany',
    'ownerStreetNumber',
    'ownerStreet',
    'ownerPostalCode',
    'ownerMunicipality',
];
export const importFieldSchema = z.enum(IMPORT_FIELDS);
/**
 * La correspondance **champ → colonne** (index à partir de 0, la colonne A valant 0). Un champ
 * absent n'est pas alimenté. Deux champs peuvent lire la même colonne ; une colonne qu'aucun champ
 * ne lit est rapportée « non conservée ».
 */
export const importMappingSchema = z.strictObject(Object.fromEntries(IMPORT_FIELDS.map((field) => [field, z.number().int().min(0).optional()])));
/** Une colonne telle que la prévisualisation la montre : son titre verbatim, et trois exemples. */
export const importColumnSchema = z.object({
    index: z.number().int().min(0),
    /** `A`, `B`… — la lettre du tableur, celle que l'humain lit. */
    letter: z.string().min(1),
    /** Le titre **tel qu'écrit**, vide pour une colonne sans titre. */
    header: z.string(),
    samples: z.array(z.string()).max(3),
});
export const importSheetPreviewSchema = z.object({
    name: z.string(),
    /** Les lignes de données — ni la ligne de titres, ni les lignes entièrement vides. */
    rowCount: z.number().int().min(0),
    columns: z.array(importColumnSchema),
    /** La correspondance proposée d'après les titres — l'écran la pré-remplit, l'humain tranche. */
    suggestedMapping: importMappingSchema,
});
/** Ce que rend la lecture d'un fichier : les feuilles importables, et celles qui ne le sont pas. */
export const importPreviewSchema = z.object({
    fileName: z.string(),
    sheets: z.array(importSheetPreviewSchema),
    /** Une feuille sans titres reconnus — « Légendes » — n'est pas proposée, mais se dit. */
    skippedSheets: z.array(z.object({ name: z.string(), reason: z.string() })),
});
/**
 * Le lot des demandes importées : un lot existant, ou un lot à créer pour ce client.
 */
export const importLotSchema = z.union([
    z.strictObject({ existingId: uuidV7Schema }),
    z.strictObject({
        code: z.string().trim().min(1),
        name: z.string().trim().min(1),
    }),
]);
/**
 * Ce que l'écran envoie au second temps, **à côté du fichier** (champ `request` du formulaire
 * multipart, en JSON).
 *
 * ⚠️ **Un lot à créer demande un client** : `Lot.client_id` est `NOT NULL`, et la campagne n'a pas
 * de sens sans son donneur d'ordre.
 *
 * ⚠️ **`clientId` et `purpose` sont des valeurs par défaut** quand la correspondance rattache les
 * colonnes `client` et `purpose` : une ligne qui nomme son client ou son type garde le sien.
 */
export const inquiryImportRequestSchema = z
    .strictObject({
    sheet: z.string().min(1),
    mapping: importMappingSchema,
    clientId: uuidV7Schema.nullish().transform((id) => id ?? null),
    /** Le type de contrôle des demandes créées — champ libre (M2, M13), comme à la création. */
    purpose: freeLabelSchema,
    lot: importLotSchema.nullish().transform((lot) => lot ?? null),
    /** L'équipe qui reçoit toutes les lignes créées (#436) ; sans elle, elles naissent reçues. */
    teamId: uuidV7Schema.nullish().transform((id) => id ?? null),
    /**
     * Les lignes que le bureau écarte sur l'écran des doublons (#589) — « Ne pas importer ». Rien
     * n'est écarté sans ce geste : « Importer quand même » est le défaut (M21).
     */
    skippedLines: z.array(z.number().int().min(1)).default([]),
    /** Les « Réf. GHP » changées sur l'écran des doublons (#589), à la place de celle de la ligne. */
    referenceOverrides: z
        .array(z.strictObject({ line: z.number().int().min(1), reference: z.string().trim().min(1) }))
        .default([]),
})
    .refine((request) => request.mapping.municipality !== undefined, {
    error: 'la colonne de la commune est obligatoire — sans elle aucune adresse ne s’écrit',
    path: ['mapping', 'municipality'],
})
    .refine((request) => request.lot === null || request.clientId !== null || 'existingId' in request.lot, {
    error: 'un lot à créer demande son client (`clientId`)',
    path: ['lot'],
});
/**
 * Le sort d'une ligne. ⚠️ **`duplicate` est écrit** : un doublon se crée et se signale (M21) — le
 * gestionnaire sait seul si c'est une contre-visite ou une erreur de saisie. Seul `error` n'écrit
 * rien, et seulement pour l'intégrité : commune inconnue ou ambiguë, commune absente.
 *
 * `skipped` n'écrit rien non plus : le bureau a écarté la ligne sur l'écran des doublons (#589).
 *
 * ⚠️ **`created` peut porter des motifs** : la ligne est écrite, mais quelque chose est à vérifier
 * — numéro de voie introuvable, client inconnu du répertoire, « Objet » non reconnu, contre-visite
 * à rattacher. Ce n'est pas un doublon, et ça se dit quand même (M21).
 */
export const IMPORT_ROW_OUTCOMES = ['created', 'duplicate', 'error', 'skipped'];
export const importRowOutcomeSchema = z.enum(IMPORT_ROW_OUTCOMES);
export const importRowResultSchema = z.object({
    /** Le numéro de la ligne **dans le tableur** — celui que l'humain retrouvera. */
    line: z.number().int().min(1),
    outcome: importRowOutcomeSchema,
    inquiryId: uuidV7Schema.nullable(),
    reference: z.string().nullable(),
    /** Pourquoi un doublon, pourquoi une erreur — des phrases, affichées telles quelles. */
    reasons: z.array(z.string()),
});
/** Le rapport d'import — ce que l'écran affiche après le second temps. */
export const inquiryImportReportSchema = z.object({
    importId: uuidV7Schema,
    fileName: z.string(),
    sheet: z.string(),
    counts: z.object({
        created: z.number().int().min(0),
        duplicates: z.number().int().min(0),
        errors: z.number().int().min(0),
        /** Les lignes écartées sur l'écran des doublons (#589), comptées à part. */
        skipped: z.number().int().min(0),
        /** Les lignes entièrement vides, ignorées — dites pour que rien ne disparaisse sans trace. */
        blank: z.number().int().min(0),
    }),
    rows: z.array(importRowResultSchema),
    /** Les colonnes du fichier qu'aucun champ ne lit — dites **une fois** chacune, pas par ligne. */
    ignoredColumns: z.array(z.object({ letter: z.string(), header: z.string() })),
    /**
     * Le secteur où les lignes sont entrées, ou `null` : sans secteur, ou un secteur à créer qu'aucune
     * ligne n'a fait naître. C'est lui que « Changer les réglages PDF et contrôle » ouvre (#661).
     */
    lotId: uuidV7Schema.nullable(),
});
/**
 * Une demande déjà en base qu'une ligne du fichier double (#589) — de quoi la reconnaître et
 * l'ouvrir avant de trancher.
 */
export const importExistingInquirySchema = z.object({
    id: uuidV7Schema,
    reference: z.string().nullable(),
    street: z.string().nullable(),
    city: z.string(),
    status: inquiryStatusSchema,
});
/** Une ligne en doublon, **avant** l'écriture : ce qu'elle porte, pourquoi, et ce qu'elle double. */
export const importDuplicateRowSchema = z.object({
    line: z.number().int().min(1),
    /** La « Réf. GHP » que la ligne recevrait — celle du fichier, sinon la composée. */
    reference: z.string().nullable(),
    street: z.string().nullable(),
    city: z.string(),
    reasons: z.array(z.string()),
    existing: z.array(importExistingInquirySchema),
});
/** Le contrôle des doublons d'un import (#589) : rien ne s'écrit, la liste est vide sans doublon. */
export const importDuplicatesSchema = z.object({ rows: z.array(importDuplicateRowSchema) });
/** La lettre de tableur d'un index de colonne : 0 → `A`, 25 → `Z`, 26 → `AA`. */
export function columnLetter(index) {
    let letter = '';
    let rest = index + 1;
    while (rest > 0) {
        const digit = (rest - 1) % 26;
        letter = String.fromCharCode(65 + digit) + letter;
        rest = Math.floor((rest - 1) / 26);
    }
    return letter;
}
/** Un titre ramené à sa forme comparable — accents, casse, ponctuation et espaces repliés. */
export function normalizeImportHeader(header) {
    return foldForSearch(header)
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();
}
/**
 * Les titres reconnus pour chaque champ, **déjà normalisés** — ceux de la liste de campagne, puis
 * ceux du « tableau des demandes » de GHP (« N° » se normalise en `n`).
 *
 * ⚠️ **« Type » n'est pas un titre du type de contrôle** : dans le tableau de GHP, c'est le type de
 * bâtiment (« Maison », « Professionnel ») ; le type de contrôle y est la colonne « Objet ».
 */
const HEADER_SYNONYMS = {
    reference: ['reference ghp', 'ref ghp', 'reference'],
    caseReference: [
        'reference dossier',
        'ref dossier',
        'reference du dossier',
        'n dossier',
        'ref client',
        'reference client',
    ],
    // ⚠️ Pas le lien de « Référence GHP » : il mène à un fichier du serveur de GHP, pas au dossier.
    caseUrl: ['lien hypertext', 'lien hypertexte', 'lien du dossier', 'lien dossier'],
    client: ['client', 'client donneur d ordre', 'donneur d ordre'],
    purpose: ['objet', 'type de controle'],
    municipality: ['commune nouvelle', 'commune', 'ville'],
    postalCode: ['code postal', 'cp'],
    streetNumber: ['n', 'n voie', 'n de voie', 'numero', 'numero de voie'],
    street: ['adresse a controler', 'adresse du controle', 'adresse', 'rue'],
    delegatedName: ['commune deleguee', 'commune delegue', 'ancienne commune'],
    cadastralReference: [
        'reference cadastrale',
        'parcelle',
        'n parcelle',
        'cadastre',
        'code insee parcelle',
    ],
    sewerNetworkType: ['unitaire', 'reseau'],
    buildingType: ['type', 'type de batiment', 'type batiment'],
    ownerLastName: ['nom', 'nom proprietaire', 'nom du proprietaire', 'nom prop'],
    ownerFirstName: ['prenom', 'prenom proprietaire', 'prenom du proprietaire', 'prenom prop'],
    // ⚠️ Pas « téléphone » seul : dans le tableau des demandes de GHP, c'est celui du contact.
    ownerPhone: [
        'tel prop',
        'tel proprietaire',
        'telephone proprietaire',
        'telephone du proprietaire',
    ],
    comment: ['commentaire', 'commentaires', 'observations'],
    requestedOn: ['date demande', 'date de la demande', 'date reception demande'],
    contactName: ['contact', 'personne a contacter'],
    // « Téléphone » seul est celui du contact, dans le tableau des demandes de GHP.
    contactPhone: ['telephone', 'tel contact', 'telephone contact'],
    applicantLastName: ['nom dem', 'nom demandeur'],
    applicantFirstName: ['prenom dem', 'prenom demandeur'],
    applicantCompany: ['raison dem', 'raison sociale demandeur'],
    applicantEmail: ['mail dem', 'mail demandeur', 'email demandeur'],
    ownerCompany: ['raison pro', 'raison prop'],
    ownerStreetNumber: ['n pro', 'n prop'],
    ownerStreet: ['rue pro', 'rue prop'],
    ownerPostalCode: ['cp pro', 'cp prop'],
    ownerMunicipality: ['commune pro', 'commune prop'],
};
/**
 * La correspondance **proposée** d'après les titres. Le premier titre reconnu d'un champ l'emporte :
 * le fichier métier porte deux « Code postal » — celui de l'adresse contrôlée, puis celui du
 * propriétaire —, et c'est le premier qui situe le contrôle.
 *
 * `comment` se propose sur la **première colonne sans titre qui porte des valeurs** : c'est la
 * colonne A du fichier métier, où le service note l'état du dossier.
 *
 * ⚠️ Elle **propose** : l'écran pré-remplit, l'humain confirme ou corrige.
 */
export function suggestImportMapping(headers, hasValues = []) {
    const mapping = {};
    const normalized = headers.map(normalizeImportHeader);
    for (const [field, synonyms] of Object.entries(HEADER_SYNONYMS)) {
        for (const synonym of synonyms) {
            const index = normalized.indexOf(synonym);
            if (index !== -1) {
                mapping[field] = index;
                break;
            }
        }
    }
    // Sans colonne « Commentaire », la colonne sans en-tête qui porte des valeurs fait la note.
    const untitled = normalized.findIndex((header, index) => header === '' && hasValues[index] === true);
    if (untitled !== -1 && mapping.comment === undefined)
        mapping.comment = untitled;
    return mapping;
}
/** Ce qui annonce une contre-visite en tête de l'« Objet », dans l'écriture de GHP. */
const COUNTER_VISIT_LABEL = 'Contre-Visite';
/** Le même, une fois normalisé : ce que l'import cherche en tête de la cellule. */
const COUNTER_VISIT_PREFIX = normalizeImportHeader(COUNTER_VISIT_LABEL);
/** Chaque type reconnu sous ses trois écritures : son code, son libellé long, son libellé court. */
const PURPOSE_BY_LABEL = new Map(INQUIRY_PURPOSES.flatMap((purpose) => [purpose, INQUIRY_PURPOSE_LABELS[purpose], INQUIRY_PURPOSE_OPTION_LABELS[purpose]].map((label) => [normalizeImportHeader(label), purpose])));
/**
 * Le type de contrôle qu'une colonne « Objet » écrit en toutes lettres — « Avant Travaux » donne
 * `pre_works`. Inconnu : `purpose` vaut `null`, et c'est à l'appelant de le dire.
 *
 * ⚠️ **Une contre-visite n'est pas un type** (M2, Q5) : c'est une nouvelle demande du type du
 * contrôle qu'elle suit. « Contre-Visite Vente » rend donc `sale`, avec `counterVisit` pour qu'elle
 * se signale — son lien au contrôle d'origine ne se fait pas à l'import (#321).
 */
export function readImportPurpose(text) {
    const normalized = normalizeImportHeader(text);
    const counterVisit = normalized.startsWith(COUNTER_VISIT_PREFIX);
    const rest = counterVisit ? normalized.slice(COUNTER_VISIT_PREFIX.length).trim() : normalized;
    return { purpose: PURPOSE_BY_LABEL.get(rest) ?? null, counterVisit };
}
/**
 * L'« Objet » dans l'écriture de GHP, celle que `readImportPurpose` relit : « Vente », et
 * « Contre-Visite Vente » pour une contre-visite **quel que soit son rang** — le rang se lit dans
 * la référence (« -CV2 »). C'est ce qui rend le CSV du tableau réimportable (#587).
 *
 * ⚠️ Ce n'est pas `counterVisitLabel` (« Vente Contre-visite 2 »), le libellé des écrans et du
 * rapport : l'import ne le relirait pas.
 */
export function importPurposeLabel(purpose, rank) {
    const type = inquiryPurposeLabel(purpose);
    if (rank === null)
        return type;
    return type === null ? COUNTER_VISIT_LABEL : `${COUNTER_VISIT_LABEL} ${type}`;
}
/**
 * Les réponses que la colonne « Unitaire ? » peut porter, une fois normalisées. ⚠️ **Lecture à
 * confirmer** (M43) : aucun fichier rempli n'a été vu, seul son titre. La colonne pose une question,
 * d'où le « oui » unitaire ; le libellé écrit en toutes lettres se lit aussi.
 */
const SEWER_NETWORK_BY_ANSWER = new Map([
    ...['oui', 'o', 'x', '1', 'vrai', 'unitaire'].map((answer) => [answer, 'combined']),
    ...['non', 'n', '0', 'faux', 'separatif'].map((answer) => [answer, 'separate']),
]);
/**
 * Le réseau du dossier qu'une colonne « Unitaire ? » répond — « Oui » donne `combined`, « Non »
 * `separate`. Vide ou illisible : `null`, et c'est à l'appelant de dire une réponse qu'il n'a pas
 * su lire.
 *
 * ⚠️ **Une case vide n'est pas un « non »** : elle vaut un réseau inconnu, que la tablette tait.
 * Et la ligne s'importe quand même — un réseau illisible ne vaut pas une demande perdue (M21).
 */
export function readImportSewerNetworkType(text) {
    return SEWER_NETWORK_BY_ANSWER.get(normalizeImportHeader(text)) ?? null;
}
/**
 * Chaque type de bâtiment sous son libellé — plus « Professionel », la graphie de 86 lignes du
 * legacy (M13).
 */
const BUILDING_TYPE_BY_LABEL = new Map([
    ...BUILDING_TYPES.map((type) => [normalizeImportHeader(BUILDING_TYPE_LABELS[type]), type]),
    ['professionel', 'professional'],
]);
/**
 * Le type de bâtiment qu'une colonne « Type » écrit en toutes lettres — « Maison » donne `house`.
 * Inconnu : `null`, et c'est à l'appelant de le dire (M21).
 */
export function readImportBuildingType(text) {
    return BUILDING_TYPE_BY_LABEL.get(normalizeImportHeader(text)) ?? null;
}
/**
 * Une feuille est-elle **importable** ? Il lui faut la commune, et de quoi reconnaître un dossier
 * ou une adresse — sinon c'est une feuille de légendes ou de calcul, qui ne se propose pas.
 */
export function isImportableMapping(mapping) {
    return (mapping.municipality !== undefined &&
        (mapping.street !== undefined || mapping.caseReference !== undefined));
}
//# sourceMappingURL=list-import.js.map